import { __, sprintf } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import { useDispatch } from '@wordpress/data';
import { PriceHtml, DokanLink, DokanPriceInput } from '@dokan/components';
import { SimpleInput, Tooltip } from '@getdokan/dokan-ui';
import { truncate } from '@dokan/utilities';
import { Product, Quote } from '../../types/quote';
import { VENDOR_QUOTES_STORE } from '../../constants';

interface QuoteProductsCustomTableProps {
    quote: Quote;
    quoteUpdated: boolean;
    setQuoteUpdated: ( updated: boolean ) => void;
}

interface PrevPrices {
    [ key: number ]: number;
}

const ProductsTable = ( {
    quote,
    quoteUpdated,
    setQuoteUpdated,
}: QuoteProductsCustomTableProps ) => {
    const dispatch = useDispatch( VENDOR_QUOTES_STORE );
    const isEditable = quote?.status === 'pending';
    const [ totalQuote, setTotalQuote ] = useState< number >( 0.0 );
    const [ products, setProducts ] = useState< Product[] >(
        quote?.products || []
    );
    const [ shippingCost, setShippingCost ] = useState< string >(
        quote?.shipping_cost?.toString() || '0'
    );

    const [ customerOfferAvailability, setCustomerOfferAvailability ] =
        useState< boolean >( false );

    const [ prevProductPrices ] = useState< PrevPrices >(
        ( quote?.products || [] ).reduce(
            ( acc: PrevPrices, product: Product ) => {
                acc[ product.id ] = parseFloat(
                    product.offer_price.toString()
                );
                return acc;
            },
            {}
        )
    );

    const [ prevShippingCost ] = useState< number >(
        parseFloat( quote?.shipping_cost?.toString() || '0' )
    );

    const checkForUpdates = () => {
        const shippingChanged =
            parseFloat( shippingCost.toString() ) !== prevShippingCost;
        const pricesChanged = products.some( ( product ) => {
            const originalPrice = prevProductPrices?.[ product?.id ];
            const currentPrice = parseFloat( product?.offer_price.toString() );
            return originalPrice !== currentPrice;
        } );

        const hasUpdates = shippingChanged || pricesChanged;
        setQuoteUpdated( hasUpdates );

        if ( ! hasUpdates ) return;

        let updatedQuote = { ...quote };
        if ( shippingChanged ) {
            updatedQuote = {
                ...updatedQuote,
                shipping_cost: parseFloat(
                    shippingChanged
                        ? shippingCost.toString()
                        : quote?.shipping_cost.toString()
                ).toFixed( window?.dokanFrontend?.currency?.precision ?? 2 ),
            };
        } else {
            updatedQuote = {
                ...updatedQuote,
                products: quote.products.map( ( product, index ) => ( {
                    ...product,
                    offer_price: parseFloat(
                        products?.[ index ]?.offer_price?.toString() ||
                            product?.offer_price.toString()
                    ).toFixed(
                        window?.dokanFrontend?.currency?.precision ?? 2
                    ),
                } ) ),
            };
        }

        dispatch.setQuote( updatedQuote );
    };

    const calculateTotals = () => {
        const total = products.reduce( ( sum, product ) => {
            return (
                sum +
                parseFloat( product.offer_price.toString() ) *
                    parseFloat( product.offer_product_quantity.toString() )
            );
        }, 0 );

        setTotalQuote( total + parseFloat( shippingCost.toString() ) );
    };

    const handleOfferPriceChange = ( index: number, value: string ) => {
        if ( value ) {
            const newValue =
                parseFloat( value )?.toFixed(
                    window?.dokanFrontend?.currency?.precision ?? 2
                ) || 0;
            const updatedProducts = [ ...products ];
            updatedProducts[ index ] = {
                ...updatedProducts[ index ],
                offer_price: newValue,
            };
            setProducts( updatedProducts );
        }
    };

    const handleShippingCostChange = (
        formattedValue: string,
        unformattedValue: number
    ) => {
        if ( formattedValue && unformattedValue ) {
            setShippingCost( formattedValue );

            if ( isEditable && ! quoteUpdated ) {
                setQuoteUpdated( unformattedValue > 0 );
            }
        }
    };

    const customerOffers = quote?.customer_info?.customer_offers;

    const isCustomerOfferAvailable = () => {
        if (
            typeof customerOffers !== 'undefined' &&
            Object.keys( customerOffers )?.length > 0
        ) {
            setCustomerOfferAvailability( true );
        }
    };

    useEffect( () => {
        calculateTotals();
        checkForUpdates();
    }, [ products, shippingCost ] );

    useEffect( isCustomerOfferAvailable, [ customerOffers ] );

    const renderProductCell = ( product: Product ) => (
        <div className="flex gap-3">
            <img
                alt={ product?.name }
                src={ product?.image_url }
                className="w-12 h-12 rounded object-cover"
            />
            <div className="self-center">
                <DokanLink
                    href={ product?.permalink }
                    className="font-semibold text-base"
                >
                    { truncate( product?.name, 20 ) }
                </DokanLink>
                { product?.sku && (
                    <p className="text-sm text-gray-500 mt-2">
                        <label className="text-gray-800">
                            { __( 'SKU: ', 'dokan' ) }
                        </label>
                        <Tooltip content={ product?.sku }>
                            <span className="cursor-help font-medium">
                                { truncate( product?.sku, 16 ) }
                            </span>
                        </Tooltip>
                    </p>
                ) }
                { product?.store_name && (
                    <p className="text-sm text-gray-500 mt-1">
                        <label className="text-gray-800">
                            { __( 'Vendor: ', 'dokan' ) }
                        </label>
                        <span className="cursor-help font-medium">
                            { truncate( product?.store_name, 20 ) }
                        </span>
                    </p>
                ) }
            </div>
        </div>
    );

    const renderOfferPriceCell = ( product: Product, index: number ) => {
        return (
            <>
                { isEditable ? (
                    <DokanPriceInput
                        label=""
                        className="w-24"
                        showAddOn={ false }
                        namespace="quote-offer-price"
                        value={ parseFloat( product?.offer_price ).toFixed(
                            window?.dokanFrontend?.currency?.precision ?? 2
                        ) }
                        onChange={ ( formattedValue, unformattedValue ) =>
                            handleOfferPriceChange(
                                index,
                                unformattedValue.toString()
                            )
                        }
                        input={ {
                            id: `offer-price-${ product.id }`,
                            name: `offer-price-${ product.id }`,
                            type: 'number',
                            placeholder: __( 'Enter offer price', 'dokan' ),
                        } }
                    />
                ) : (
                    <PriceHtml price={ product?.offer_price } />
                ) }
            </>
        );
    };

    const renderCustomerOfferCell = ( index: number ) => {
        const customerOfferValues = Object.values( customerOffers || {} );
        return <PriceHtml price={ customerOfferValues?.[ index ] ?? 0 } />;
    };

    return (
        <div className="space-y-6">
            <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
                <table className="dataviews-view-table has-comfortable-density w-full text-sm text-left text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3">
                                <button
                                    className={ `components-button dataviews-view-table-header-button is-compact is-tertiary` }
                                >
                                    { __( 'Product', 'dokan' ) }
                                </button>
                            </th>
                            <th scope="col" className="px-6 py-3">
                                <button
                                    className={ `components-button dataviews-view-table-header-button is-compact is-tertiary` }
                                >
                                    { __( 'Original Price', 'dokan' ) }
                                </button>
                            </th>
                            { customerOfferAvailability && (
                                <th scope="col" className="px-6 py-3">
                                    <button
                                        className={ `components-button dataviews-view-table-header-button is-compact is-tertiary` }
                                    >
                                        { __( 'Customer Offer', 'dokan' ) }
                                    </button>
                                </th>
                            ) }
                            <th scope="col" className="px-6 py-3">
                                <button
                                    className={ `components-button dataviews-view-table-header-button is-compact is-tertiary` }
                                >
                                    { sprintf(
                                        /* translators: %s: My or Your offer */
                                        __( '%s Offer', 'dokan' ),
                                        isEditable ? 'My' : 'Your'
                                    ) }
                                </button>
                            </th>
                            <th scope="col" className="px-6 py-3">
                                <button
                                    className={ `components-button dataviews-view-table-header-button is-compact is-tertiary` }
                                >
                                    { __( 'Qty', 'dokan' ) }
                                </button>
                            </th>
                            <th scope="col" className="px-6 py-3">
                                <button
                                    className={ `components-button dataviews-view-table-header-button is-compact is-tertiary` }
                                >
                                    { __( 'Total', 'dokan' ) }
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        { products.map( ( product, index ) => (
                            <tr
                                key={ product.id }
                                className="bg-white border-b hover:bg-gray-50"
                            >
                                <td className="px-6 py-4">
                                    { renderProductCell( product ) }
                                </td>
                                <td className="px-6 py-4">
                                    <span
                                        className={ `dataviews-view-table__cell-content-wrapper` }
                                    >
                                        <PriceHtml price={ product?.price } />
                                    </span>
                                </td>
                                { customerOfferAvailability && (
                                    <td className="px-6 py-4">
                                        <span
                                            className={ `dataviews-view-table__cell-content-wrapper` }
                                        >
                                            { renderCustomerOfferCell( index ) }
                                        </span>
                                    </td>
                                ) }
                                <td className="px-6 py-4">
                                    <span
                                        className={ `dataviews-view-table__cell-content-wrapper` }
                                    >
                                        { renderOfferPriceCell(
                                            product,
                                            index
                                        ) }
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <span
                                        className={ `dataviews-view-table__cell-content-wrapper` }
                                    >
                                        { product?.offer_product_quantity }
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <span
                                        className={ `dataviews-view-table__cell-content-wrapper` }
                                    >
                                        <PriceHtml
                                            price={
                                                parseFloat(
                                                    product?.offer_price.toString()
                                                ) *
                                                parseFloat(
                                                    product?.offer_product_quantity.toString()
                                                )
                                            }
                                        />
                                    </span>
                                </td>
                            </tr>
                        ) ) }
                    </tbody>
                </table>
            </div>

            <div className="mt-4 rounded-lg border border-gray-200 bg-[#fdfdfd] shadow-md px-6 py-4">
                <div className="flex flex-col space-y-4">
                    { /* Shipping Cost Row */ }
                    <div className="flex justify-between items-center border-b border-gray-200 pb-4">
                        <span className="font-medium text-gray-800">
                            { __( 'Shipping Cost', 'dokan' ) }
                        </span>
                        <div>
                            { isEditable ? (
                                <DokanPriceInput
                                    label=""
                                    className="w-32"
                                    showAddOn={ false }
                                    value={ parseFloat( shippingCost ).toFixed(
                                        window?.dokanFrontend?.currency
                                            ?.precision ?? 2
                                    ) }
                                    namespace="quote-shipping-cost"
                                    onChange={ handleShippingCostChange }
                                    input={ {
                                        min: 0,
                                        id: 'shipping-cost',
                                        name: 'shipping-cost',
                                        type: 'number',
                                        placeholder: __(
                                            'Enter amount',
                                            'dokan'
                                        ),
                                    } }
                                />
                            ) : (
                                <PriceHtml
                                    price={ parseFloat( shippingCost ) }
                                />
                            ) }
                        </div>
                    </div>

                    { /* Total Row */ }
                    <div className="flex justify-between items-center">
                        <span className="font-semibold text-gray-900">
                            { __( 'Total', 'dokan' ) }
                        </span>
                        <div className="font-semibold">
                            <PriceHtml price={ totalQuote } />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductsTable;
